import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useToast } from '../../../components/ui/use-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { Id } from '../../../convex/_generated/dataModel';
import { useAuth } from '../../../lib/auth/AuthContext';

interface ProjectFormProps {
  initialData?: {
    id: string;
    name: string;
    slug: string;
  };
  onSuccess?: () => void;
}

export const ProjectForm: React.FC<ProjectFormProps> = ({ initialData: propInitialData, onSuccess }) => {
  // Get projectId from URL params for edit mode
  const { projectId } = useParams<{ projectId: string }>();
  const { toast } = useToast();
  const { useAuthenticatedQuery, useAuthenticatedMutation } = useAuth();
  
  // Fetch project data if in edit mode
  const projectData = useAuthenticatedQuery(
    'projects:getById',
    projectId ? { projectId } : "skip"
  );
  
  // Initialize state with either props data, fetched data, or empty values
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    slug?: string;
  }>({});

  // Update form when project data is loaded
  useEffect(() => {
    if (propInitialData) {
      setName(propInitialData.name);
      setSlug(propInitialData.slug);
    } else if (projectData) {
      setName(projectData.name || '');
      setSlug(projectData.slug || '');
    }
  }, [propInitialData, projectData]);

  const navigate = useNavigate();
  const createProject = useAuthenticatedMutation('projects:create');
  const createTestProject = useAuthenticatedMutation('projects:createTest');
  const updateProject = useAuthenticatedMutation('projects:update');

  const validateForm = () => {
    const newErrors: {
      name?: string;
      slug?: string;
    } = {};
    let isValid = true;

    if (!name.trim()) {
      newErrors.name = 'Project name is required';
      isValid = false;
    }

    if (!slug.trim()) {
      newErrors.slug = 'URL slug is required';
      isValid = false;
    } else if (!/^[a-z0-9-]+$/.test(slug)) {
      newErrors.slug = 'URL slug can only contain lowercase letters, numbers, and hyphens';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    
    // Auto-generate slug if user hasn't manually entered one or if names matched before
    if (!slug || (propInitialData && propInitialData.name === propInitialData.slug)) {
      setSlug(value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (projectId || propInitialData) {
        // Update existing project
        await updateProject({
          projectId: projectId || propInitialData?.id || '',
          name,
          slug,
        });
        toast({
          title: 'Success',
          description: 'Project updated successfully',
        });
      } else {
        // Create new project using the test method (bypasses auth)
        await createTestProject({
          name,
          slug,
        });
        toast({
          title: 'Success',
          description: 'Project created successfully',
        });
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/admin/projects');
      }
    } catch (error) {
      console.error('Error saving project:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save project',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Project Name
        </label>
        <Input
          id="name"
          value={name}
          onChange={handleNameChange}
          disabled={isSubmitting}
          className={errors.name ? 'border-red-500' : ''}
        />
        {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
          URL Slug
        </label>
        <Input
          id="slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
          disabled={isSubmitting}
          className={errors.slug ? 'border-red-500' : ''}
        />
        {errors.slug && <p className="text-sm text-red-500">{errors.slug}</p>}
        <p className="text-xs text-gray-500">
          This will be used in the URL: domain.com/en/{slug}
        </p>
      </div>

      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/admin/projects')}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : projectId || propInitialData ? 'Update Project' : 'Create Project'}
        </Button>
      </div>
    </form>
  );
}; 